/**
 * ManageTab — admin interface for checklists, rules, and prompt libraries.
 *
 * Uses feathers AlertDialog to confirm destructive actions (replaces
 * window.confirm which throws NotFoundError in Office.js task panes).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Input,
  Textarea,
  Label,
  Alert,
  AlertDescription,
  Badge,
  Separator,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@e14c/feathers';
import {
  Add,
  ChevronDown,
  ChevronUp,
  Edit,
  TrashCan,
  Renew,
  CheckboxChecked,
  Book,
  IbmWatsonAssistant,
} from '@e14c/feathers/icons';

import { useAdminStore, useContentAdminStore, useCriticStore, useReviewStore } from '../store';
import {
  listChecklists,
  getChecklist,
  createChecklist,
  deleteChecklist,
  createRule,
  updateRule,
  addRuleToChecklist,
  removeRuleFromChecklist,
} from '../api/checklist';
import {
  listLibraries,
  listPrompts,
  createLibrary,
  createPrompt,
  updatePrompt,
  deleteLibrary,
  deletePrompt,
  listPaperIndexes,
  getUserSettings,
  updateUserSettings,
} from '../api/contentGen';
import {
  listPersonas,
  createPersona,
  updatePersona,
  deletePersona,
} from '../api/critic';
import type {
  ChecklistDetailOut,
  RuleOut,
  PromptLibraryOut,
  PromptOut,
  PromptVariableDef,
  GenerationApproach,
  PersonaOut,
  PaperIndexOut,
} from '../types';
import { GENERATION_APPROACH_LABELS } from '../types';
import { useTabLayoutStyles } from '../styles/tabLayout';
import { TabScrollArea } from '../components/TabScrollArea';

// ---------------------------------------------------------------------------
// Shared form field wrapper
// ---------------------------------------------------------------------------

const FieldRow: React.FC<{
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, required, hint, children }) => (
  <div className="space-y-1.5">
    <Label>
      {label}
      {required && <span className="text-destructive ml-0.5">*</span>}
    </Label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

// Shared styled native select
const NativeSelect: React.FC<
  React.SelectHTMLAttributes<HTMLSelectElement>
> = ({ className = '', ...props }) => (
  <select
    className={`flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  />
);

// Inline spinner
const Spinner: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <span
    style={{ width: size, height: size }}
    className="inline-block animate-spin rounded-full border-2 border-muted-foreground border-t-transparent"
  />
);

// ---------------------------------------------------------------------------
// New Checklist form
// ---------------------------------------------------------------------------

const NewChecklistForm: React.FC<{
  onCreated: (cl: ChecklistDetailOut) => void;
  onCancel: () => void;
}> = ({ onCreated, onCancel }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const resp = await createChecklist({
        name: name.trim(),
        description: description.trim() || null,
      });
      if (!resp.data) throw new Error(resp.error?.message ?? 'Failed to create checklist');
      onCreated({ ...resp.data, rules: [] });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-border rounded-md p-3 bg-background flex flex-col gap-3">
      <p className="text-sm font-semibold">New Checklist</p>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <FieldRow label="Name" required>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. FDA 510(k) Requirements"
          disabled={saving}
        />
      </FieldRow>

      <FieldRow label="Description">
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description"
          disabled={saving}
        />
      </FieldRow>

      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!name.trim() || saving}
        >
          {saving ? <Spinner size={14} /> : null}
          {saving ? 'Creating…' : 'Create'}
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// New Rule form
// ---------------------------------------------------------------------------

interface NewRuleFormProps {
  checklistId: string;
  onCreated: (rule: RuleOut) => void;
  onCancel: () => void;
}

const NewRuleForm: React.FC<NewRuleFormProps> = ({
  checklistId,
  onCreated,
  onCancel,
}) => {
  const [title, setTitle] = useState('');
  const [criteria, setCriteria] = useState('');
  const [description, setDescription] = useState('');
  const [condition, setCondition] = useState('');
  const [sectionMatch, setSectionMatch] = useState('');
  const [sectionMatchType, setSectionMatchType] = useState<
    'exact' | 'regex' | 'document' | ''
  >('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!title.trim() || !criteria.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const ruleResp = await createRule({
        title: title.trim(),
        criteria: criteria.trim(),
        description: description.trim() || null,
        condition: condition.trim() || null,
        section_match: sectionMatch.trim() || null,
        section_match_type: sectionMatchType || null,
      });
      if (!ruleResp.data) throw new Error(ruleResp.error?.message ?? 'Failed to create rule');

      const attachResp = await addRuleToChecklist(checklistId, ruleResp.data.id);
      if (!attachResp.data) throw new Error(attachResp.error?.message ?? 'Failed to attach rule');

      onCreated(ruleResp.data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-border rounded-md p-3 bg-background flex flex-col gap-3">
      <p className="text-sm font-semibold">New Rule</p>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <FieldRow label="Title" required>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. 510(k) Substantial Equivalence Claim"
          disabled={saving}
        />
      </FieldRow>

      <FieldRow
        label="Criteria"
        required
        hint="Describe what the document must demonstrate or contain."
      >
        <Textarea
          value={criteria}
          onChange={(e) => setCriteria(e.target.value)}
          placeholder="The document must include a predicate device comparison…"
          rows={3}
          disabled={saving}
        />
      </FieldRow>

      <Button
        variant="ghost"
        size="sm"
        className="self-start"
        onClick={() => setShowAdvanced((v) => !v)}
      >
        {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        {showAdvanced ? 'Hide advanced' : 'Show advanced'}
      </Button>

      {showAdvanced && (
        <>
          <FieldRow label="Description">
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional human-readable description"
              disabled={saving}
            />
          </FieldRow>

          <FieldRow
            label="Condition"
            hint="Optional — LLM evaluates this first; rule is skipped (N/A) if false."
          >
            <Input
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
              placeholder="e.g. Document is a 510(k) submission"
              disabled={saving}
            />
          </FieldRow>

          <FieldRow
            label="Section match"
            hint="Limit evaluation to a specific section of the document."
          >
            <Input
              value={sectionMatch}
              onChange={(e) => setSectionMatch(e.target.value)}
              placeholder="e.g. Substantial Equivalence"
              disabled={saving}
            />
          </FieldRow>

          <FieldRow label="Section match type">
            <NativeSelect
              value={sectionMatchType}
              onChange={(e) =>
                setSectionMatchType(
                  e.target.value as 'exact' | 'regex' | 'document' | '',
                )
              }
              disabled={saving}
            >
              <option value="">— Any section</option>
              <option value="exact">Exact title match</option>
              <option value="regex">Regex match</option>
              <option value="document">Whole document</option>
            </NativeSelect>
          </FieldRow>
        </>
      )}

      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!title.trim() || !criteria.trim() || saving}
        >
          {saving ? <Spinner size={14} /> : null}
          {saving ? 'Saving…' : 'Add Rule'}
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Edit Rule form
// ---------------------------------------------------------------------------

interface EditRuleFormProps {
  rule: RuleOut;
  onUpdated: (rule: RuleOut) => void;
  onCancel: () => void;
}

const EditRuleForm: React.FC<EditRuleFormProps> = ({
  rule,
  onUpdated,
  onCancel,
}) => {
  const [title, setTitle] = useState(rule.title);
  const [criteria, setCriteria] = useState(rule.criteria);
  const [description, setDescription] = useState(rule.description ?? '');
  const [condition, setCondition] = useState(rule.condition ?? '');
  const [sectionMatch, setSectionMatch] = useState(rule.section_match ?? '');
  const [sectionMatchType, setSectionMatchType] = useState<
    'exact' | 'regex' | 'document' | ''
  >(rule.section_match_type ?? '');
  const [showAdvanced, setShowAdvanced] = useState(
    !!(rule.description || rule.condition || rule.section_match || rule.section_match_type),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!title.trim() || !criteria.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const resp = await updateRule(rule.id, {
        title: title.trim(),
        criteria: criteria.trim(),
        description: description.trim() || null,
        condition: condition.trim() || null,
        section_match: sectionMatch.trim() || null,
        section_match_type: sectionMatchType || null,
      });
      if (!resp.data) throw new Error(resp.error?.message ?? 'Failed to update rule');
      onUpdated(resp.data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-border rounded-md p-3 bg-background flex flex-col gap-3">
      <p className="text-sm font-semibold">Edit Rule</p>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <FieldRow label="Title" required>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={saving}
        />
      </FieldRow>

      <FieldRow
        label="Criteria"
        required
        hint="Describe what the document must demonstrate or contain."
      >
        <Textarea
          value={criteria}
          onChange={(e) => setCriteria(e.target.value)}
          rows={3}
          disabled={saving}
        />
      </FieldRow>

      <Button
        variant="ghost"
        size="sm"
        className="self-start"
        onClick={() => setShowAdvanced((v) => !v)}
      >
        {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        {showAdvanced ? 'Hide advanced' : 'Show advanced'}
      </Button>

      {showAdvanced && (
        <>
          <FieldRow label="Description">
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional human-readable description"
              disabled={saving}
            />
          </FieldRow>

          <FieldRow
            label="Condition"
            hint="Optional — LLM evaluates this first; rule is skipped (N/A) if false."
          >
            <Input
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
              placeholder="e.g. Document is a 510(k) submission"
              disabled={saving}
            />
          </FieldRow>

          <FieldRow
            label="Section match"
            hint="Limit evaluation to a specific section of the document."
          >
            <Input
              value={sectionMatch}
              onChange={(e) => setSectionMatch(e.target.value)}
              placeholder="e.g. Substantial Equivalence"
              disabled={saving}
            />
          </FieldRow>

          <FieldRow label="Section match type">
            <NativeSelect
              value={sectionMatchType}
              onChange={(e) =>
                setSectionMatchType(
                  e.target.value as 'exact' | 'regex' | 'document' | '',
                )
              }
              disabled={saving}
            >
              <option value="">— Any section</option>
              <option value="exact">Exact title match</option>
              <option value="regex">Regex match</option>
              <option value="document">Whole document</option>
            </NativeSelect>
          </FieldRow>
        </>
      )}

      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!title.trim() || !criteria.trim() || saving}
        >
          {saving ? <Spinner size={14} /> : null}
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Checklist row
// ---------------------------------------------------------------------------

interface ChecklistRowProps {
  checklist: ChecklistDetailOut;
  isExpanded: boolean;
  onToggle: () => void;
  onRuleAdded: (rule: RuleOut) => void;
  onRuleUpdated: (rule: RuleOut) => void;
  onRuleRemoved: (ruleId: string) => void;
  onDeleted: () => void;
}

const ChecklistRow: React.FC<ChecklistRowProps> = ({
  checklist,
  isExpanded,
  onToggle,
  onRuleAdded,
  onRuleUpdated,
  onRuleRemoved,
  onDeleted,
}) => {
  const [showNewRuleForm, setShowNewRuleForm] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  const handleRemoveRule = async (ruleId: string) => {
    setRemovingId(ruleId);
    try {
      await removeRuleFromChecklist(checklist.id, ruleId);
      onRuleRemoved(ruleId);
    } finally {
      setRemovingId(null);
    }
  };

  const doDelete = async () => {
    setIsDeleting(true);
    setRowError(null);
    try {
      await deleteChecklist(checklist.id);
      onDeleted();
    } catch (err: unknown) {
      setRowError(err instanceof Error ? err.message : String(err));
      setIsDeleting(false);
    }
  };

  return (
    <div className="border border-border rounded-md overflow-hidden mb-1.5">
      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete checklist?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &quot;{checklist.name}&quot; and
              remove all of its rule associations. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={doDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div
        className="flex items-center gap-2 px-3 py-2 cursor-pointer bg-background hover:bg-muted/40"
        onClick={onToggle}
        role="button"
        aria-expanded={isExpanded}
      >
        <CheckboxChecked size={18} className="text-muted-foreground shrink-0" />
        <span className="flex-1 text-sm font-semibold truncate">{checklist.name}</span>
        <Badge variant="secondary">{checklist.rules.length}</Badge>
        <Button
          variant="ghost"
          size="icon-sm"
          disabled={isDeleting}
          onClick={(e) => {
            e.stopPropagation();
            setConfirmDeleteOpen(true);
          }}
          title="Delete checklist"
        >
          {isDeleting ? <Spinner size={14} /> : <TrashCan size={14} />}
        </Button>
        {isExpanded ? (
          <ChevronUp size={16} className="shrink-0" />
        ) : (
          <ChevronDown size={16} className="shrink-0" />
        )}
      </div>

      {isExpanded && (
        <div className="px-3 py-2 bg-muted/30 border-t border-border flex flex-col gap-2">
          {rowError && (
            <Alert variant="destructive">
              <AlertDescription>{rowError}</AlertDescription>
            </Alert>
          )}

          {checklist.description && (
            <p className="text-xs text-muted-foreground">{checklist.description}</p>
          )}

          {checklist.rules.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No rules yet</p>
          ) : (
            checklist.rules.map((rule) =>
              editingRuleId === rule.id ? (
                <EditRuleForm
                  key={rule.id}
                  rule={rule}
                  onUpdated={(updated) => {
                    onRuleUpdated(updated);
                    setEditingRuleId(null);
                  }}
                  onCancel={() => setEditingRuleId(null)}
                />
              ) : (
                <div key={rule.id} className="flex items-center gap-2 py-0.5">
                  <span className="flex-1 text-sm truncate">{rule.title}</span>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setEditingRuleId(rule.id)}
                    title="Edit rule"
                  >
                    <Edit size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    disabled={removingId === rule.id}
                    onClick={() => handleRemoveRule(rule.id)}
                    title="Remove rule from checklist"
                  >
                    {removingId === rule.id ? (
                      <Spinner size={14} />
                    ) : (
                      <TrashCan size={14} />
                    )}
                  </Button>
                </div>
              ),
            )
          )}

          <Separator className="my-1" />

          {showNewRuleForm ? (
            <NewRuleForm
              checklistId={checklist.id}
              onCreated={(rule) => {
                onRuleAdded(rule);
                setShowNewRuleForm(false);
              }}
              onCancel={() => setShowNewRuleForm(false)}
            />
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="self-start"
              onClick={() => setShowNewRuleForm(true)}
            >
              <Add size={16} />
              Add Rule
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// New Library form
// ---------------------------------------------------------------------------

const NewLibraryForm: React.FC<{
  onCreated: (lib: PromptLibraryOut) => void;
  onCancel: () => void;
}> = ({ onCreated, onCancel }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const lib = await createLibrary({
        name: name.trim(),
        description: description.trim() || null,
      });
      onCreated(lib);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-border rounded-md p-3 bg-background flex flex-col gap-3">
      <p className="text-sm font-semibold">New Prompt Library</p>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <FieldRow label="Name" required>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Oncology Module"
          disabled={saving}
        />
      </FieldRow>

      <FieldRow label="Description">
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description"
          disabled={saving}
        />
      </FieldRow>

      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!name.trim() || saving}
        >
          {saving ? <Spinner size={14} /> : null}
          {saving ? 'Creating…' : 'Create'}
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// New Prompt form
// ---------------------------------------------------------------------------

interface NewPromptFormProps {
  libraryId: string;
  nextPosition: number;
  onCreated: (prompt: PromptOut) => void;
  onCancel: () => void;
}

const NewPromptForm: React.FC<NewPromptFormProps> = ({
  libraryId,
  nextPosition,
  onCreated,
  onCancel,
}) => {
  const [title, setTitle] = useState('');
  const [template, setTemplate] = useState('');
  const [description, setDescription] = useState('');
  const [sectionMatch, setSectionMatch] = useState('');
  const [sectionMatchType, setSectionMatchType] = useState<
    'exact' | 'regex' | 'any' | ''
  >('');
  const [variableNames, setVariableNames] = useState('');
  const [approach, setApproach] = useState<GenerationApproach>('llm');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!title.trim() || !template.trim()) return;
    setSaving(true);
    setError(null);

    const variables: PromptVariableDef[] = variableNames
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((name) => ({
        name,
        description: null,
        default: null,
        required: false,
      }));

    try {
      const prompt = await createPrompt(libraryId, {
        title: title.trim(),
        template: template.trim(),
        description: description.trim() || null,
        section_match: sectionMatch.trim() || null,
        section_match_type: sectionMatchType || null,
        variables,
        position: nextPosition,
        approach,
      });
      onCreated(prompt);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-border rounded-md p-3 bg-background flex flex-col gap-3">
      <p className="text-sm font-semibold">New Prompt</p>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <FieldRow label="Title" required>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Efficacy Results"
          disabled={saving}
        />
      </FieldRow>

      <FieldRow
        label="Template"
        required
        hint="Jinja2 template. Use {{ variable_name }} for variables."
      >
        <Textarea
          value={template}
          onChange={(e) => setTemplate(e.target.value)}
          placeholder="Write the efficacy results for {{ indication }} in {{ region }}..."
          rows={4}
          disabled={saving}
        />
      </FieldRow>

      <FieldRow
        label="Generation service"
        hint="LLM uses LiteLLM directly. Aries uses the Edison data-analysis agent. PaperQA uses a SharePoint literature index."
      >
        <NativeSelect
          value={approach}
          onChange={(e) =>
            setApproach(e.target.value as GenerationApproach)
          }
          disabled={saving}
        >
          <option value="llm">LLM</option>
          <option value="aries">Aries</option>
          <option value="paperqa">PaperQA (literature)</option>
        </NativeSelect>
      </FieldRow>

      <Button
        variant="ghost"
        size="sm"
        className="self-start"
        onClick={() => setShowAdvanced((v) => !v)}
      >
        {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        {showAdvanced ? 'Hide advanced' : 'Show advanced'}
      </Button>

      {showAdvanced && (
        <>
          <FieldRow label="Description">
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              disabled={saving}
            />
          </FieldRow>

          <FieldRow
            label="Variables"
            hint="Comma-separated variable names declared in the template."
          >
            <Input
              value={variableNames}
              onChange={(e) => setVariableNames(e.target.value)}
              placeholder="indication, region, study_name"
              disabled={saving}
            />
          </FieldRow>

          <FieldRow
            label="Section match"
            hint="Limit this prompt to a specific document section."
          >
            <Input
              value={sectionMatch}
              onChange={(e) => setSectionMatch(e.target.value)}
              placeholder="e.g. Efficacy Results"
              disabled={saving}
            />
          </FieldRow>

          <FieldRow label="Section match type">
            <NativeSelect
              value={sectionMatchType}
              onChange={(e) =>
                setSectionMatchType(
                  e.target.value as 'exact' | 'regex' | 'any' | '',
                )
              }
              disabled={saving}
            >
              <option value="">— Any section</option>
              <option value="exact">Exact title match</option>
              <option value="regex">Regex match</option>
              <option value="any">Any section</option>
            </NativeSelect>
          </FieldRow>
        </>
      )}

      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!title.trim() || !template.trim() || saving}
        >
          {saving ? <Spinner size={14} /> : null}
          {saving ? 'Saving…' : 'Add Prompt'}
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Edit Prompt form
// ---------------------------------------------------------------------------

interface EditPromptFormProps {
  libraryId: string;
  prompt: PromptOut;
  onUpdated: (prompt: PromptOut) => void;
  onCancel: () => void;
}

const EditPromptForm: React.FC<EditPromptFormProps> = ({
  libraryId,
  prompt,
  onUpdated,
  onCancel,
}) => {
  const [title, setTitle] = useState(prompt.title);
  const [template, setTemplate] = useState(prompt.template);
  const [description, setDescription] = useState(prompt.description ?? '');
  const [sectionMatch, setSectionMatch] = useState(prompt.section_match ?? '');
  const [sectionMatchType, setSectionMatchType] = useState<
    'exact' | 'regex' | 'any' | ''
  >(prompt.section_match_type ?? '');
  const [variableNames, setVariableNames] = useState(
    prompt.variables.map((v) => v.name).join(', '),
  );
  const [approach, setApproach] = useState<GenerationApproach>(
    prompt.approach ?? 'llm',
  );
  const [showAdvanced, setShowAdvanced] = useState(
    !!(
      prompt.description ||
      prompt.variables.length > 0 ||
      prompt.section_match ||
      prompt.section_match_type
    ),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!title.trim() || !template.trim()) return;
    setSaving(true);
    setError(null);

    const variables: PromptVariableDef[] = variableNames
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((name) => ({
        name,
        description: null,
        default: null,
        required: false,
      }));

    try {
      const updated = await updatePrompt(libraryId, prompt.id, {
        title: title.trim(),
        template: template.trim(),
        description: description.trim() || null,
        section_match: sectionMatch.trim() || null,
        section_match_type: sectionMatchType || null,
        variables,
        approach,
      });
      onUpdated(updated);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-border rounded-md p-3 bg-background flex flex-col gap-3">
      <p className="text-sm font-semibold">Edit Prompt</p>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <FieldRow label="Title" required>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={saving}
        />
      </FieldRow>

      <FieldRow
        label="Template"
        required
        hint="Jinja2 template. Use {{ variable_name }} for variables."
      >
        <Textarea
          value={template}
          onChange={(e) => setTemplate(e.target.value)}
          rows={4}
          disabled={saving}
        />
      </FieldRow>

      <FieldRow
        label="Generation service"
        hint="LLM uses LiteLLM directly. Aries uses the Edison data-analysis agent. PaperQA uses a SharePoint literature index."
      >
        <NativeSelect
          value={approach}
          onChange={(e) =>
            setApproach(e.target.value as GenerationApproach)
          }
          disabled={saving}
        >
          <option value="llm">LLM</option>
          <option value="aries">Aries</option>
          <option value="paperqa">PaperQA (literature)</option>
        </NativeSelect>
      </FieldRow>

      <Button
        variant="ghost"
        size="sm"
        className="self-start"
        onClick={() => setShowAdvanced((v) => !v)}
      >
        {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        {showAdvanced ? 'Hide advanced' : 'Show advanced'}
      </Button>

      {showAdvanced && (
        <>
          <FieldRow label="Description">
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              disabled={saving}
            />
          </FieldRow>

          <FieldRow
            label="Variables"
            hint="Comma-separated variable names declared in the template."
          >
            <Input
              value={variableNames}
              onChange={(e) => setVariableNames(e.target.value)}
              placeholder="indication, region, study_name"
              disabled={saving}
            />
          </FieldRow>

          <FieldRow
            label="Section match"
            hint="Limit this prompt to a specific document section."
          >
            <Input
              value={sectionMatch}
              onChange={(e) => setSectionMatch(e.target.value)}
              placeholder="e.g. Efficacy Results"
              disabled={saving}
            />
          </FieldRow>

          <FieldRow label="Section match type">
            <NativeSelect
              value={sectionMatchType}
              onChange={(e) =>
                setSectionMatchType(
                  e.target.value as 'exact' | 'regex' | 'any' | '',
                )
              }
              disabled={saving}
            >
              <option value="">— Any section</option>
              <option value="exact">Exact title match</option>
              <option value="regex">Regex match</option>
              <option value="any">Any section</option>
            </NativeSelect>
          </FieldRow>
        </>
      )}

      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!title.trim() || !template.trim() || saving}
        >
          {saving ? <Spinner size={14} /> : null}
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Library row
// ---------------------------------------------------------------------------

interface LibraryRowProps {
  library: PromptLibraryOut;
  prompts: PromptOut[] | undefined;
  isExpanded: boolean;
  onToggle: () => void;
  onPromptsLoaded: (prompts: PromptOut[]) => void;
  onPromptAdded: (prompt: PromptOut) => void;
  onPromptUpdated: (prompt: PromptOut) => void;
  onPromptRemoved: (promptId: string) => void;
  onLibraryDeleted: () => void;
}

const LibraryRow: React.FC<LibraryRowProps> = ({
  library,
  prompts,
  isExpanded,
  onToggle,
  onPromptsLoaded,
  onPromptAdded,
  onPromptUpdated,
  onPromptRemoved,
  onLibraryDeleted,
}) => {
  const [showNewPromptForm, setShowNewPromptForm] = useState(false);
  const [editingPromptId, setEditingPromptId] = useState<string | null>(null);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const [removingPromptId, setRemovingPromptId] = useState<string | null>(null);
  const [isDeletingLibrary, setIsDeletingLibrary] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  // Lazy-load prompts when expanded for the first time
  useEffect(() => {
    if (isExpanded && prompts === undefined && !isLoadingPrompts) {
      setIsLoadingPrompts(true);
      setRowError(null);
      listPrompts(library.id, 100, 0)
        .then((data) => onPromptsLoaded(data.items))
        .catch((err: unknown) =>
          setRowError(err instanceof Error ? err.message : String(err)),
        )
        .finally(() => setIsLoadingPrompts(false));
    }
  }, [isExpanded, prompts, library.id, isLoadingPrompts, onPromptsLoaded]);

  const handleDeletePrompt = async (promptId: string) => {
    setRemovingPromptId(promptId);
    try {
      await deletePrompt(library.id, promptId);
      onPromptRemoved(promptId);
    } catch (err: unknown) {
      setRowError(err instanceof Error ? err.message : String(err));
    } finally {
      setRemovingPromptId(null);
    }
  };

  const doDeleteLibrary = async () => {
    setIsDeletingLibrary(true);
    try {
      await deleteLibrary(library.id);
      onLibraryDeleted();
    } catch (err: unknown) {
      setRowError(err instanceof Error ? err.message : String(err));
      setIsDeletingLibrary(false);
    }
  };

  return (
    <div className="border border-border rounded-md overflow-hidden mb-1.5">
      {/* Confirm-delete dialog (no window.confirm — throws in Office.js) */}
      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete library?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &quot;{library.name}&quot; and all
              its prompts. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={doDeleteLibrary}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Row header */}
      <div
        className="flex items-center gap-2 px-3 py-2 cursor-pointer bg-background hover:bg-muted/40"
        onClick={onToggle}
        role="button"
        aria-expanded={isExpanded}
      >
        <Book size={18} className="text-muted-foreground shrink-0" />
        <span className="flex-1 text-sm font-semibold truncate">{library.name}</span>
        <Badge variant="default">{library.prompt_count}</Badge>
        <Button
          variant="ghost"
          size="icon-sm"
          disabled={isDeletingLibrary}
          onClick={(e) => {
            e.stopPropagation();
            setConfirmDeleteOpen(true);
          }}
          title="Delete library"
        >
          {isDeletingLibrary ? <Spinner size={14} /> : <TrashCan size={14} />}
        </Button>
        {isExpanded ? (
          <ChevronUp size={16} className="shrink-0" />
        ) : (
          <ChevronDown size={16} className="shrink-0" />
        )}
      </div>

      {isExpanded && (
        <div className="px-3 py-2 bg-muted/30 border-t border-border flex flex-col gap-2">
          {library.description && (
            <p className="text-xs text-muted-foreground">{library.description}</p>
          )}

          {rowError && (
            <Alert variant="destructive">
              <AlertDescription>{rowError}</AlertDescription>
            </Alert>
          )}

          {isLoadingPrompts && (
            <div className="flex items-center gap-2">
              <Spinner size={14} />
              <span className="text-xs text-muted-foreground">Loading prompts…</span>
            </div>
          )}

          {!isLoadingPrompts && prompts?.length === 0 && (
            <p className="text-sm text-muted-foreground italic">No prompts yet</p>
          )}

          {!isLoadingPrompts &&
            prompts?.map((p) =>
              editingPromptId === p.id ? (
                <EditPromptForm
                  key={p.id}
                  libraryId={library.id}
                  prompt={p}
                  onUpdated={(updated) => {
                    onPromptUpdated(updated);
                    setEditingPromptId(null);
                  }}
                  onCancel={() => setEditingPromptId(null)}
                />
              ) : (
                <div key={p.id} className="flex items-center gap-2 py-0.5">
                  <span className="flex-1 text-sm truncate">{p.title}</span>
                  <Badge variant="outline">
                    {GENERATION_APPROACH_LABELS[p.approach ?? 'llm']}
                  </Badge>
                  {p.section_match_type && p.section_match && (
                    <Badge variant="secondary">
                      {p.section_match_type === 'exact'
                        ? '='
                        : p.section_match_type === 'regex'
                          ? '~'
                          : '*'}
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => {
                      setShowNewPromptForm(false);
                      setEditingPromptId(p.id);
                    }}
                    title="Edit prompt"
                  >
                    <Edit size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    disabled={removingPromptId === p.id}
                    onClick={() => handleDeletePrompt(p.id)}
                    title="Delete prompt"
                  >
                    {removingPromptId === p.id ? (
                      <Spinner size={14} />
                    ) : (
                      <TrashCan size={14} />
                    )}
                  </Button>
                </div>
              ),
            )}

          <Separator className="my-1" />

          {showNewPromptForm ? (
            <NewPromptForm
              libraryId={library.id}
              nextPosition={prompts?.length ?? 0}
              onCreated={(p) => {
                onPromptAdded(p);
                setShowNewPromptForm(false);
              }}
              onCancel={() => setShowNewPromptForm(false)}
            />
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="self-start"
              onClick={() => {
                setEditingPromptId(null);
                setShowNewPromptForm(true);
              }}
            >
              <Add size={16} />
              Add Prompt
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// New Persona form
// ---------------------------------------------------------------------------

const NewPersonaForm: React.FC<{
  onCreated: (persona: PersonaOut) => void;
  onCancel: () => void;
}> = ({ onCreated, onCancel }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [focusAreas, setFocusAreas] = useState('');
  const [model, setModel] = useState('');
  const [maxTopItems, setMaxTopItems] = useState('5');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!name.trim() || !systemPrompt.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const focusAreasList = focusAreas
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const resp = await createPersona({
        name: name.trim(),
        description: description.trim() || null,
        system_prompt: systemPrompt.trim(),
        focus_areas: focusAreasList,
        model: model.trim() || null,
        max_top_items: parseInt(maxTopItems, 10) || 5,
      });
      if (!resp.data)
        throw new Error(resp.error?.message ?? 'Failed to create persona');
      onCreated(resp.data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-border rounded-md p-3 bg-background flex flex-col gap-3">
      <p className="text-sm font-semibold">New Persona</p>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <FieldRow label="Name" required>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Medical Writer"
          disabled={saving}
        />
      </FieldRow>

      <FieldRow
        label="System prompt"
        required
        hint="Describe how this persona should evaluate the text."
      >
        <Textarea
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          placeholder="You are an expert medical writer reviewing regulatory submissions for clarity, accuracy, and compliance…"
          rows={4}
          disabled={saving}
        />
      </FieldRow>

      <Button
        variant="ghost"
        size="sm"
        className="self-start"
        onClick={() => setShowAdvanced((v) => !v)}
      >
        {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        {showAdvanced ? 'Hide advanced' : 'Show advanced'}
      </Button>

      {showAdvanced && (
        <>
          <FieldRow label="Description">
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              disabled={saving}
            />
          </FieldRow>

          <FieldRow
            label="Focus areas"
            hint="Comma-separated aspects this persona should prioritise."
          >
            <Input
              value={focusAreas}
              onChange={(e) => setFocusAreas(e.target.value)}
              placeholder="clarity, structure, regulatory compliance"
              disabled={saving}
            />
          </FieldRow>

          <FieldRow
            label="Model override"
            hint="Leave blank to use the service default."
          >
            <Input
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="e.g. claude-sonnet-4-6"
              disabled={saving}
            />
          </FieldRow>

          <FieldRow label="Max top items" hint="Maximum items per section (default 5).">
            <Input
              type="number"
              min={1}
              max={20}
              value={maxTopItems}
              onChange={(e) => setMaxTopItems(e.target.value)}
              disabled={saving}
            />
          </FieldRow>
        </>
      )}

      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!name.trim() || !systemPrompt.trim() || saving}
        >
          {saving ? <Spinner size={14} /> : null}
          {saving ? 'Creating…' : 'Create'}
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Edit Persona form
// ---------------------------------------------------------------------------

interface EditPersonaFormProps {
  persona: PersonaOut;
  onUpdated: (persona: PersonaOut) => void;
  onCancel: () => void;
}

const EditPersonaForm: React.FC<EditPersonaFormProps> = ({
  persona,
  onUpdated,
  onCancel,
}) => {
  const [name, setName] = useState(persona.name);
  const [description, setDescription] = useState(persona.description ?? '');
  const [systemPrompt, setSystemPrompt] = useState(persona.system_prompt);
  const [focusAreas, setFocusAreas] = useState(
    persona.focus_areas.join(', '),
  );
  const [model, setModel] = useState(persona.model ?? '');
  const [maxTopItems, setMaxTopItems] = useState(String(persona.max_top_items));
  const [showAdvanced, setShowAdvanced] = useState(
    !!(persona.description || persona.focus_areas.length > 0 || persona.model),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!name.trim() || !systemPrompt.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const focusAreasList = focusAreas
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const resp = await updatePersona(persona.id, {
        name: name.trim(),
        description: description.trim() || null,
        system_prompt: systemPrompt.trim(),
        focus_areas: focusAreasList,
        model: model.trim() || null,
        max_top_items: parseInt(maxTopItems, 10) || 5,
      });
      if (!resp.data)
        throw new Error(resp.error?.message ?? 'Failed to update persona');
      onUpdated(resp.data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-border rounded-md p-3 bg-background flex flex-col gap-3">
      <p className="text-sm font-semibold">Edit Persona</p>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <FieldRow label="Name" required>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={saving}
        />
      </FieldRow>

      <FieldRow
        label="System prompt"
        required
        hint="Describe how this persona should evaluate the text."
      >
        <Textarea
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          rows={4}
          disabled={saving}
        />
      </FieldRow>

      <Button
        variant="ghost"
        size="sm"
        className="self-start"
        onClick={() => setShowAdvanced((v) => !v)}
      >
        {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        {showAdvanced ? 'Hide advanced' : 'Show advanced'}
      </Button>

      {showAdvanced && (
        <>
          <FieldRow label="Description">
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              disabled={saving}
            />
          </FieldRow>

          <FieldRow
            label="Focus areas"
            hint="Comma-separated aspects this persona should prioritise."
          >
            <Input
              value={focusAreas}
              onChange={(e) => setFocusAreas(e.target.value)}
              placeholder="clarity, structure, regulatory compliance"
              disabled={saving}
            />
          </FieldRow>

          <FieldRow
            label="Model override"
            hint="Leave blank to use the service default."
          >
            <Input
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="e.g. claude-sonnet-4-6"
              disabled={saving}
            />
          </FieldRow>

          <FieldRow label="Max top items" hint="Maximum items per section (default 5).">
            <Input
              type="number"
              min={1}
              max={20}
              value={maxTopItems}
              onChange={(e) => setMaxTopItems(e.target.value)}
              disabled={saving}
            />
          </FieldRow>
        </>
      )}

      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!name.trim() || !systemPrompt.trim() || saving}
        >
          {saving ? <Spinner size={14} /> : null}
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Persona row
// ---------------------------------------------------------------------------

interface PersonaRowProps {
  persona: PersonaOut;
  isExpanded: boolean;
  onToggle: () => void;
  onUpdated: (persona: PersonaOut) => void;
  onDeleted: () => void;
}

const PersonaRow: React.FC<PersonaRowProps> = ({
  persona,
  isExpanded,
  onToggle,
  onUpdated,
  onDeleted,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  const doDelete = async () => {
    setIsDeleting(true);
    setRowError(null);
    try {
      await deletePersona(persona.id);
      onDeleted();
    } catch (err: unknown) {
      setRowError(err instanceof Error ? err.message : String(err));
      setIsDeleting(false);
    }
  };

  if (isEditing) {
    return (
      <EditPersonaForm
        persona={persona}
        onUpdated={(updated) => {
          onUpdated(updated);
          setIsEditing(false);
        }}
        onCancel={() => setIsEditing(false)}
      />
    );
  }

  return (
    <div className="border border-border rounded-md overflow-hidden mb-1.5">
      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete persona?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &quot;{persona.name}&quot;. Any
              existing critique jobs that reference it will be unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={doDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Row header */}
      <div
        className="flex items-center gap-2 px-3 py-2 cursor-pointer bg-background hover:bg-muted/40"
        onClick={onToggle}
        role="button"
        aria-expanded={isExpanded}
      >
        <IbmWatsonAssistant
          size={18}
          className="text-muted-foreground shrink-0"
        />
        <span className="flex-1 text-sm font-semibold truncate">
          {persona.name}
        </span>
        {persona.focus_areas.length > 0 && (
          <Badge variant="secondary">{persona.focus_areas.length} areas</Badge>
        )}
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={(e) => {
            e.stopPropagation();
            setIsEditing(true);
          }}
          title="Edit persona"
        >
          <Edit size={14} />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          disabled={isDeleting}
          onClick={(e) => {
            e.stopPropagation();
            setConfirmDeleteOpen(true);
          }}
          title="Delete persona"
        >
          {isDeleting ? <Spinner size={14} /> : <TrashCan size={14} />}
        </Button>
        {isExpanded ? (
          <ChevronUp size={16} className="shrink-0" />
        ) : (
          <ChevronDown size={16} className="shrink-0" />
        )}
      </div>

      {isExpanded && (
        <div className="px-3 py-2 bg-muted/30 border-t border-border flex flex-col gap-1.5">
          {rowError && (
            <Alert variant="destructive">
              <AlertDescription>{rowError}</AlertDescription>
            </Alert>
          )}

          {persona.description && (
            <p className="text-xs text-muted-foreground">{persona.description}</p>
          )}

          <p className="text-xs text-foreground/80 line-clamp-3">
            {persona.system_prompt}
          </p>

          {persona.focus_areas.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-0.5">
              {persona.focus_areas.map((area) => (
                <Badge key={area} variant="outline">
                  {area}
                </Badge>
              ))}
            </div>
          )}

          <div className="flex gap-3 text-xs text-muted-foreground mt-0.5">
            {persona.model && <span>Model: {persona.model}</span>}
            <span>Top items: {persona.max_top_items}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Literature index settings
// ---------------------------------------------------------------------------

const LiteratureIndexSection: React.FC = () => {
  const [indexes, setIndexes] = useState<PaperIndexOut[]>([]);
  const [selectedIndexId, setSelectedIndexId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [indexList, settings] = await Promise.all([
        listPaperIndexes(),
        getUserSettings(),
      ]);
      setIndexes(indexList);
      setSelectedIndexId(settings.default_paper_index_id ?? '');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await updateUserSettings({
        default_paper_index_id: selectedIndexId || null,
      });
      setSaved(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold">Literature Index</span>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => void load()}
          disabled={loading}
          title="Refresh"
        >
          <Renew size={16} />
        </Button>
      </div>

      <p className="text-xs text-muted-foreground mb-3">
        Select a SharePoint RAG index from Platform Data Storage for fact-checking
        and PaperQA prompt generation.
      </p>

      {error && (
        <Alert variant="destructive" className="mb-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {saved && (
        <Alert className="mb-3">
          <AlertDescription>Default literature index saved.</AlertDescription>
        </Alert>
      )}

      {loading && indexes.length === 0 ? (
        <div className="flex items-center gap-2 py-2">
          <Spinner size={14} />
          <span className="text-xs text-muted-foreground">Loading indexes…</span>
        </div>
      ) : indexes.length === 0 ? (
        <p className="text-sm text-muted-foreground italic py-2">
          No SharePoint indexes found. Build one in SharePoint Integration and sync
          it to Data Storage.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <FieldRow label="Default index">
            <NativeSelect
              value={selectedIndexId}
              onChange={(e) => setSelectedIndexId(e.target.value)}
              disabled={saving}
            >
              <option value="">— None selected —</option>
              {indexes.map((idx) => (
                <option key={idx.id} value={idx.id}>
                  {idx.site_hostname && idx.site_path
                    ? `${idx.site_hostname}${idx.site_path}`
                    : idx.name}
                </option>
              ))}
            </NativeSelect>
          </FieldRow>
          <Button size="sm" className="self-start" onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Saving…' : 'Save default index'}
          </Button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Content control section
// ---------------------------------------------------------------------------

const ContentControlSection: React.FC = () => {
  const [title, setTitle] = useState('');
  const [selectedText, setSelectedText] = useState<string | null>(null);
  const [inserting, setInserting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const readSelection = useCallback(async () => {
    if (typeof Word === 'undefined' || !Word.run) return;
    try {
      await Word.run(async (context) => {
        const sel = context.document.getSelection();
        sel.load('text');
        await context.sync();
        const trimmed = sel.text?.trim() ?? '';
        setSelectedText(trimmed.length > 0 ? trimmed : null);
      });
    } catch {
      // transient Office.js errors during rapid selection changes
    }
  }, []);

  useEffect(() => {
    if (typeof Office === 'undefined') return;

    const handler = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => void readSelection(), 300);
    };

    try {
      Office.context.document.addHandlerAsync(
        Office.EventType.DocumentSelectionChanged,
        handler,
      );
    } catch { /* not fatal */ }

    void readSelection();

    return () => {
      try {
        Office.context.document.removeHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
          { handler },
          () => {},
        );
      } catch { /* ignore */ }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, [readSelection]);

  const handleInsert = async () => {
    if (!title.trim() || !selectedText) return;
    setInserting(true);
    setError(null);
    setSuccess(false);
    try {
      await Word.run(async (context) => {
        const range = context.document.getSelection();
        const cc = range.insertContentControl();
        cc.title = title.trim();
        cc.tag = title.trim();
        cc.appearance = Word.ContentControlAppearance.boundingBox;
        await context.sync();
      });
      setSuccess(true);
      setTitle('');
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      successTimerRef.current = setTimeout(() => setSuccess(false), 3000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setInserting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold">Content Controls</span>
      </div>

      <div className="border border-border rounded-md p-3 bg-background flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label>Selected text</Label>
          {selectedText ? (
            <p className="text-xs bg-muted rounded px-2 py-1.5 text-foreground/70 break-words line-clamp-3">
              {selectedText.length > 150
                ? selectedText.slice(0, 150) + '…'
                : selectedText}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground italic">
              No text selected — highlight text in your document first.
            </p>
          )}
        </div>

        <FieldRow label="Title" required>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Efficacy Summary"
            disabled={inserting}
          />
        </FieldRow>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert>
            <AlertDescription>Content control inserted.</AlertDescription>
          </Alert>
        )}

        <Button
          onClick={() => void handleInsert()}
          disabled={!title.trim() || !selectedText || inserting}
          size="sm"
          className="self-end"
        >
          {inserting ? <Spinner size={14} /> : null}
          {inserting ? 'Inserting…' : 'Insert Content Control'}
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const ManageTab: React.FC = () => {
  const layout = useTabLayoutStyles();

  // ── Checklist store ────────────────────────────────────────────────────────
  const checklists = useAdminStore((s) => s.checklists);
  const selectedChecklistId = useAdminStore((s) => s.selectedChecklistId);
  const isLoading = useAdminStore((s) => s.isLoading);
  const globalError = useAdminStore((s) => s.globalError);
  const setChecklists = useAdminStore((s) => s.setChecklists);
  const addChecklist = useAdminStore((s) => s.addChecklist);
  const removeChecklist = useAdminStore((s) => s.removeChecklist);
  const setSelectedChecklistId = useAdminStore((s) => s.setSelectedChecklistId);
  const setIsLoading = useAdminStore((s) => s.setIsLoading);
  const setGlobalError = useAdminStore((s) => s.setGlobalError);

  const removeChecklistFromReview = useReviewStore((s) => s.removeChecklist);

  // ── Content-admin store ────────────────────────────────────────────────────
  const libraries = useContentAdminStore((s) => s.libraries);
  const promptsByLibrary = useContentAdminStore((s) => s.promptsByLibrary);
  const selectedLibraryId = useContentAdminStore((s) => s.selectedLibraryId);
  const isLoadingLibraries = useContentAdminStore((s) => s.isLoading);
  const contentAdminError = useContentAdminStore((s) => s.contentAdminError);
  const setLibraries = useContentAdminStore((s) => s.setLibraries);
  const addLibrary = useContentAdminStore((s) => s.addLibrary);
  const removeLibrary = useContentAdminStore((s) => s.removeLibrary);
  const setSelectedLibraryId = useContentAdminStore(
    (s) => s.setSelectedLibraryId,
  );
  const setPromptsForLibrary = useContentAdminStore(
    (s) => s.setPromptsForLibrary,
  );
  const addPromptToLibrary = useContentAdminStore((s) => s.addPromptToLibrary);
  const updatePromptInLibrary = useContentAdminStore(
    (s) => s.updatePromptInLibrary,
  );
  const removePromptFromLibrary = useContentAdminStore(
    (s) => s.removePromptFromLibrary,
  );
  const setIsLoadingLibraries = useContentAdminStore((s) => s.setIsLoading);
  const setContentAdminError = useContentAdminStore(
    (s) => s.setContentAdminError,
  );

  // ── Critic store ───────────────────────────────────────────────────────────
  const personas = useCriticStore((s) => s.personas);
  const selectedPersonaId = useCriticStore((s) => s.selectedPersonaId);
  const isLoadingPersonas = useCriticStore((s) => s.isLoadingPersonas);
  const personasError = useCriticStore((s) => s.personasError);
  const setPersonas = useCriticStore((s) => s.setPersonas);
  const addPersona = useCriticStore((s) => s.addPersona);
  const updatePersonaInList = useCriticStore((s) => s.updatePersonaInList);
  const removePersona = useCriticStore((s) => s.removePersona);
  const setSelectedPersonaId = useCriticStore((s) => s.setSelectedPersonaId);
  const setIsLoadingPersonas = useCriticStore((s) => s.setIsLoadingPersonas);
  const setPersonasError = useCriticStore((s) => s.setPersonasError);

  const [showNewChecklistForm, setShowNewChecklistForm] = useState(false);
  const [showNewLibraryForm, setShowNewLibraryForm] = useState(false);
  const [showNewPersonaForm, setShowNewPersonaForm] = useState(false);

  // ── Load checklists ────────────────────────────────────────────────────────
  const loadChecklists = useCallback(async () => {
    setIsLoading(true);
    setGlobalError(null);
    try {
      const resp = await listChecklists(50, 0);
      if (!resp.data) throw new Error(resp.error?.message ?? 'Failed to load checklists');
      const detailed = await Promise.all(
        resp.data.checklists.map(async (cl) => {
          const detail = await getChecklist(cl.id);
          return detail.data ?? { ...cl, rules: [] };
        }),
      );
      setChecklists(detailed);
    } catch (err: unknown) {
      setGlobalError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, setGlobalError, setChecklists]);

  // ── Load libraries ─────────────────────────────────────────────────────────
  const loadLibraries = useCallback(async () => {
    setIsLoadingLibraries(true);
    setContentAdminError(null);
    try {
      const data = await listLibraries(50, 0);
      setLibraries(data.items);
    } catch (err: unknown) {
      setContentAdminError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoadingLibraries(false);
    }
  }, [setIsLoadingLibraries, setContentAdminError, setLibraries]);

  // ── Load personas ──────────────────────────────────────────────────────────
  const loadPersonas = useCallback(async () => {
    setIsLoadingPersonas(true);
    setPersonasError(null);
    try {
      const resp = await listPersonas(50, 0);
      if (!resp.data)
        throw new Error(resp.error?.message ?? 'Failed to load personas');
      setPersonas(resp.data.personas);
    } catch (err: unknown) {
      setPersonasError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoadingPersonas(false);
    }
  }, [setIsLoadingPersonas, setPersonasError, setPersonas]);

  useEffect(() => {
    if (checklists.length === 0 && !isLoading) loadChecklists();
    if (libraries.length === 0 && !isLoadingLibraries) loadLibraries();
    if (personas.length === 0 && !isLoadingPersonas) loadPersonas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading && checklists.length === 0) {
    return (
      <div className={layout.panel}>
        <div className={layout.centered}>
          <span className="inline-block size-8 animate-spin rounded-full border-[3px] border-muted-foreground border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className={layout.panel}>
      <TabScrollArea>
        {/* Checklist global error */}
        {globalError && (
          <Alert variant="destructive">
            <AlertDescription className="flex items-center justify-between gap-2">
              <span>{globalError}</span>
              <Button variant="ghost" size="sm" onClick={loadChecklists}>
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* ── Checklists section ───────────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold">Checklists</span>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={loadChecklists}
                title="Refresh"
              >
                <Renew size={16} />
              </Button>
              {!showNewChecklistForm && (
                <Button
                  size="sm"
                  onClick={() => setShowNewChecklistForm(true)}
                >
                  <Add size={16} />
                  New
                </Button>
              )}
            </div>
          </div>

          {showNewChecklistForm && (
            <NewChecklistForm
              onCreated={(cl) => {
                addChecklist(cl);
                setShowNewChecklistForm(false);
              }}
              onCancel={() => setShowNewChecklistForm(false)}
            />
          )}

          {checklists.length === 0 && !isLoading && (
            <p className="text-sm text-muted-foreground italic py-2">
              No checklists yet. Click New to create one.
            </p>
          )}

          {checklists.map((cl) => (
            <ChecklistRow
              key={cl.id}
              checklist={cl}
              isExpanded={selectedChecklistId === cl.id}
              onToggle={() => setSelectedChecklistId(cl.id)}
              onRuleAdded={(rule) =>
                setChecklists(
                  checklists.map((c) =>
                    c.id === cl.id
                      ? { ...c, rules: [...c.rules, rule] }
                      : c,
                  ),
                )
              }
              onRuleUpdated={(updated) =>
                setChecklists(
                  checklists.map((c) =>
                    c.id === cl.id
                      ? { ...c, rules: c.rules.map((r) => r.id === updated.id ? updated : r) }
                      : c,
                  ),
                )
              }
              onRuleRemoved={(ruleId) =>
                setChecklists(
                  checklists.map((c) =>
                    c.id === cl.id
                      ? { ...c, rules: c.rules.filter((r) => r.id !== ruleId) }
                      : c,
                  ),
                )
              }
              onDeleted={() => {
                removeChecklist(cl.id);
                removeChecklistFromReview(cl.id);
              }}
            />
          ))}
        </div>

        <Separator />

        {/* ── Prompt Libraries section ─────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold">Prompt Libraries</span>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={loadLibraries}
                disabled={isLoadingLibraries}
                title="Refresh"
              >
                <Renew size={16} />
              </Button>
              {!showNewLibraryForm && (
                <Button
                  size="sm"
                  onClick={() => setShowNewLibraryForm(true)}
                >
                  <Add size={16} />
                  New
                </Button>
              )}
            </div>
          </div>

          {contentAdminError && (
            <Alert variant="destructive">
              <AlertDescription className="flex items-center justify-between gap-2">
                <span>{contentAdminError}</span>
                <Button variant="ghost" size="sm" onClick={loadLibraries}>
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {showNewLibraryForm && (
            <NewLibraryForm
              onCreated={(lib) => {
                addLibrary(lib);
                setShowNewLibraryForm(false);
              }}
              onCancel={() => setShowNewLibraryForm(false)}
            />
          )}

          {isLoadingLibraries && libraries.length === 0 && (
            <div className="flex items-center gap-2 py-2">
              <Spinner size={14} />
              <span className="text-xs text-muted-foreground">Loading libraries…</span>
            </div>
          )}

          {!isLoadingLibraries && libraries.length === 0 && (
            <p className="text-sm text-muted-foreground italic py-2">
              No prompt libraries yet. Click New to create one.
            </p>
          )}

          {libraries.map((lib) => (
            <LibraryRow
              key={lib.id}
              library={lib}
              prompts={promptsByLibrary[lib.id]}
              isExpanded={selectedLibraryId === lib.id}
              onToggle={() => setSelectedLibraryId(lib.id)}
              onPromptsLoaded={(ps) => setPromptsForLibrary(lib.id, ps)}
              onPromptAdded={(p) => addPromptToLibrary(lib.id, p)}
              onPromptUpdated={(p) => updatePromptInLibrary(lib.id, p)}
              onPromptRemoved={(pId) => removePromptFromLibrary(lib.id, pId)}
              onLibraryDeleted={() => removeLibrary(lib.id)}
            />
          ))}
        </div>

        <Separator />

        <LiteratureIndexSection />

        <Separator />

        {/* ── Content Controls section ─────────────────────────────────────── */}
        <ContentControlSection />

        <Separator />

        {/* ── Critics section ──────────────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold">Critics</span>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={loadPersonas}
                disabled={isLoadingPersonas}
                title="Refresh"
              >
                <Renew size={16} />
              </Button>
              {!showNewPersonaForm && (
                <Button
                  size="sm"
                  onClick={() => setShowNewPersonaForm(true)}
                >
                  <Add size={16} />
                  New
                </Button>
              )}
            </div>
          </div>

          {personasError && (
            <Alert variant="destructive">
              <AlertDescription className="flex items-center justify-between gap-2">
                <span>{personasError}</span>
                <Button variant="ghost" size="sm" onClick={loadPersonas}>
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {showNewPersonaForm && (
            <NewPersonaForm
              onCreated={(persona) => {
                addPersona(persona);
                setShowNewPersonaForm(false);
              }}
              onCancel={() => setShowNewPersonaForm(false)}
            />
          )}

          {isLoadingPersonas && personas.length === 0 && (
            <div className="flex items-center gap-2 py-2">
              <Spinner size={14} />
              <span className="text-xs text-muted-foreground">
                Loading personas…
              </span>
            </div>
          )}

          {!isLoadingPersonas && personas.length === 0 && (
            <p className="text-sm text-muted-foreground italic py-2">
              No critic personas yet. Click New to create one.
            </p>
          )}

          {personas.map((persona) => (
            <PersonaRow
              key={persona.id}
              persona={persona}
              isExpanded={selectedPersonaId === persona.id}
              onToggle={() => setSelectedPersonaId(persona.id)}
              onUpdated={(updated) => updatePersonaInList(updated)}
              onDeleted={() => removePersona(persona.id)}
            />
          ))}
        </div>
      </TabScrollArea>
    </div>
  );
};
